import { Link, useNavigate } from "react-router-dom";
import { LogOut, Settings, UserCog } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePendingUsers } from "@/context/PendingUsersContext";
import { Logo } from "@/components/layout/Logo";
import { ConnectionIndicator } from "@/components/layout/ConnectionIndicator";
import { ROLE_LABELS } from "@/constants/workflow";

export function TopBar({ children, subtitle }) {
  const { user, capabilities, logout } = useAuth();
  const { pending } = usePendingUsers();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-slate-200 bg-white/92 px-4 backdrop-blur-md [@media(max-height:520px)]:h-11 sm:px-6">
      <div className="flex min-w-0 items-center gap-3">
        <Link to="/" className="flex items-center gap-2.5" data-testid="brand-home-link">
          <Logo size={26} />
          <span className="font-display text-sm font-semibold tracking-tight text-slate-900 sm:text-base">
            OkyanusHub
          </span>
        </Link>
        {subtitle ? (
          <>
            <span className="text-slate-400">/</span>
            <span className="mono truncate text-xs text-slate-600 sm:text-sm">{subtitle}</span>
          </>
        ) : null}
      </div>

      <div className="flex items-center gap-2 sm:gap-4">
        {children}
        <ConnectionIndicator />
        <div className="hidden text-right sm:block">
          <p className="text-xs font-medium text-slate-800">{user?.full_name}</p>
          <p className="mono text-[10px] uppercase tracking-wider text-slate-500">
            {(user?.roles || []).map((r) => ROLE_LABELS[r] || r).join(" · ") || "No role"}
          </p>
        </div>
        {capabilities?.is_guest ? (
          <span
            data-testid="guest-readonly-badge"
            title="Guest role — read-only access"
            className="mono rounded-full bg-slate-200 px-2 py-0.5 text-[9px] uppercase tracking-wider text-slate-600"
          >
            Read only
          </span>
        ) : null}
        <button
          data-testid="settings-nav-button"
          onClick={() => navigate("/settings")}
          className="okn-tool grid h-8 w-8 place-items-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-cyan-700"
          title="My settings"
        >
          <UserCog size={16} />
        </button>
        {capabilities?.is_admin ? (
          <button
            data-testid="admin-nav-button"
            onClick={() => navigate("/admin")}
            className="okn-tool relative grid h-8 w-8 place-items-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-cyan-700"
            title={
              pending
                ? `${pending} user${pending === 1 ? "" : "s"} waiting for approval`
                : "Administration"
            }
          >
            <Settings size={16} />
            {pending ? (
              <span
                data-testid="pending-users-badge"
                className="mono absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-red-600 px-1 text-[9px] font-semibold text-white"
              >
                {pending > 9 ? "9+" : pending}
              </span>
            ) : null}
          </button>
        ) : null}
        <button
          data-testid="logout-button"
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
          className="okn-tool grid h-8 w-8 place-items-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-red-700"
          title="Sign out"
        >
          <LogOut size={16} />
        </button>
      </div>
    </header>
  );
}
